import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { ReferralActivationStatus, type ReferralGetFromSlugRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton } from 'noddi-ui-web';
import { customerRoutes, hasErrorCode, isAxiosError } from 'noddi-util';
import {
  AppType,
  getExternalAppUrl,
  mapCouponTemplatesToClaimDiscounts,
  ReferralCustomerView,
  toWhitelabelBrand,
  useAuthContext
} from 'noddi-web';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiErrorPageWithTrans } from '../../components/Error/ApiErrorPageWithTrans';
import { Error404PageWithTranslations } from '../../components/Error/Error404PageWithTranslations';
import { ContentWrapperCustomerApp } from '../../components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '../../components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerLoginModal } from '../../components/Login/CustomerLoginModal';
import { ActivateReferral } from '../../components/Referrals/ActivateReferral';
import { CopyReferralLink } from '../../components/Referrals/CopyReferralLink';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import {
  useShowUserGroupDialog,
  useUserGroupDialogActions,
  useUserGroupDialogStore
} from '../../stores/userGroupDialogStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getSelectableUserGroups } from '../../utils/userGroupContext';

export const Referrals = () => {
  const params = useParams<{ slug: string }>();
  const { userData } = useAuthContext();
  const { currentBrand } = useCurrentBrand();
  const safeSlug = params.slug?.toLocaleLowerCase();

  const { data, isPending, error } = noddiAsync.useGet({
    type: URLKeys.getReferralFromSlug,
    input: { slug: safeSlug! }
  });

  const needsRedirect = data != null && data.brandDomain !== currentBrand;

  useEffect(() => {
    if (!needsRedirect || !data) {
      return;
    }
    const brand = toWhitelabelBrand(data.brandDomain);
    const targetUrl = getExternalAppUrl({
      externalApp: AppType.Customer,
      route: customerRoutes.referralsInfo.getPath({ slug: safeSlug! }),
      ...(brand ? { brand } : {})
    });
    window.location.href = targetUrl;
  }, [needsRedirect, data, safeSlug]);

  if (isPending || needsRedirect) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (error && hasErrorCode(error, 'referral_no_longer_available')) {
    return (
      <ContentWrapperCustomerApp hideBackButton title={t`Referral unavailable`}>
        <p className='text-muted-foreground'>
          <Trans>This referral link is no longer available.</Trans>
        </p>
      </ContentWrapperCustomerApp>
    );
  }

  if (error && isAxiosError(error) && error.response?.status === 404) {
    return <Error404PageWithTranslations />;
  }

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  if (!data.isEnabled) {
    return (
      <ContentWrapperCustomerApp hideBackButton title={t`Referral unavailable`}>
        <p className='text-muted-foreground'>
          <Trans>This referral program is currently not available.</Trans>
        </p>
      </ContentWrapperCustomerApp>
    );
  }

  return <ReferralContent key={`${userData?.user.id}:${data.id}`} referral={data} />;
};

const ReferralContent = ({ referral }: { referral: ReferralGetFromSlugRecord }) => {
  const { isLoggedIn, currentUserGroupId, userData } = useAuthContext();
  const selectableGroups = userData ? getSelectableUserGroups(userData) : [];
  const requiresProfileChoice =
    selectableGroups.length > 0 &&
    (selectableGroups.length > 1 || !selectableGroups.some((group) => group.id === currentUserGroupId));
  const [profileSelectionComplete, setProfileSelectionComplete] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const showUserGroupDialog = useShowUserGroupDialog();
  const { openUserGroupDialog } = useUserGroupDialogActions();

  useEffect(() => {
    if (!requiresProfileChoice) {
      return;
    }
    const unsubscribe = useUserGroupDialogStore.subscribe((state, previous) => {
      if (previous.showUserGroupDialog && !state.showUserGroupDialog) {
        setProfileSelectionComplete(true);
      }
    });
    openUserGroupDialog();
    return unsubscribe;
  }, [requiresProfileChoice, openUserGroupDialog]);

  const profileReady =
    selectableGroups.length > 0 && (!requiresProfileChoice || profileSelectionComplete) && !showUserGroupDialog;
  const {
    data: eligibility,
    isLoading: isEligibilityLoading,
    error: eligibilityError
  } = noddiAsync.useGet({
    type: URLKeys.getReferralEligibility,
    input: { referralId: referral.id, userGroupId: currentUserGroupId!, brandDomain: referral.brandDomain },
    queryConfig: { enabled: isLoggedIn && !!currentUserGroupId && profileReady }
  });
  if (isLoggedIn && selectableGroups.length === 0) {
    return (
      <ContentWrapperCustomerApp hideBackButton title={t`Referral unavailable`}>
        <p>
          <Trans>No customer profile is available for this account.</Trans>
        </p>
      </ContentWrapperCustomerApp>
    );
  }
  if (isLoggedIn && (!profileReady || !currentUserGroupId || isEligibilityLoading)) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (eligibilityError && !eligibility) {
    return <ApiErrorPageWithTrans error={eligibilityError} />;
  }
  const isOwnReferral = isLoggedIn && eligibility?.status === ReferralActivationStatus.OWN_REFERRAL;
  const commonTranslations = getCommonTranslations();

  const discounts = mapCouponTemplatesToClaimDiscounts(referral.couponGroup?.couponTemplates ?? [], () => undefined);

  const action = isOwnReferral ? (
    <div className='flex w-full flex-col gap-3'>
      <p className='text-sm text-muted-foreground'>
        <Trans>You cannot activate your own referral. Share this link with friends instead.</Trans>
      </p>
      <CopyReferralLink referralCode={referral.slug} />
    </div>
  ) : isLoggedIn ? (
    currentUserGroupId && eligibility ? (
      <ActivateReferral
        key={currentUserGroupId}
        referral={referral}
        status={eligibility.status}
        userGroupId={currentUserGroupId}
      />
    ) : null
  ) : (
    <NoddiButton className='w-full md:w-fit md:self-end' onClick={() => setOpenLoginModal(true)}>
      <Trans>Get your referral discount</Trans>
    </NoddiButton>
  );

  return (
    <ContentWrapperCustomerApp hideBackButton>
      <ReferralCustomerView
        // eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate emoji in customer-facing copy: it carries the tone of the message, it is not a status a NoddiIcon could show
        title={`${t`Hurray`}🥳`}
        subtitle={
          isOwnReferral ? t`This is your referral link` : `${t`You have been invited by`} ${referral.ownerName}`
        }
        discounts={discounts}
        action={action}
        translations={{
          activated: commonTranslations.activated,
          used: commonTranslations.used,
          amount: commonTranslations.amount,
          discount: commonTranslations.discount,
          free: commonTranslations.free
        }}
      />

      <CustomerLoginModal
        onClose={() => setOpenLoginModal(false)}
        open={openLoginModal}
        title={t`To activate your benefits, we first need to verify your phone number`}
        description={t`We will create a user for you, if you do not have one`}
      />
    </ContentWrapperCustomerApp>
  );
};
